import { isEmpty } from 'lodash'

const SET_USER = 'SET_USER'
const SET_REPORT = 'SET_REPORT'
const SET_PRODUCT_ORDER = 'SET_PRODUCT_ORDER'
const SET_ORDERS = 'SET_ORDERS'
const SET_CERTIFICATES = 'SET_CERTIFICATES'

const SET_TOTALS = 'cart/SET_TOTALS'

function fromCa(stateCode = '') {
  return ['ca', 'california'].includes(stateCode.trim().toLowerCase())
}

function planDuration(planId) {
  return planId === '90' ? 3 : 0
}

export const state = () => ({
  details: {},
  nutritionist: {},
  report: {},
  productOrder: [],
  recommendedItems: [],
  certificates: [],
  orders: [],
  orderCount: 0
})

function parseUser(user = {}) {
  if (isEmpty(user)) {
    return 'guest'
  }

  if (user.isCanceled) {
    return 'canceled'
  }

  if (user.subscription) {
    return 'subscriber'
  }

  return 'user'
}

export const mutations = {
  [SET_USER](state, payload) {
    state.details = payload
  },

  [SET_REPORT](state, payload) {
    const { nutritionist, recommendedItems, ...report } = payload

    state.nutritionist = nutritionist
    state.report = report
    state.recommendedItems = recommendedItems
  },

  [SET_CERTIFICATES](state, payload) {
    state.certificates = payload
  },

  [SET_ORDERS](state, { data, total }) {
    state.orders = data
    state.orderCount = total
  },

  [SET_PRODUCT_ORDER](state, payload) {
    state.productOrder = payload
  }
}

export const actions = {
  async getReport({ commit, state }) {
    if (isEmpty(state.report)) {
      try {
        const response = await this.$axios.$get('/api/user/report')

        commit(SET_REPORT, response)
      } catch (err) {
        console.error(err)
      }
    }
  },

  async getCertificates({ commit }) {
    if (isEmpty(state.certificates)) {
      try {
        const response = await this.$axios.$get('/api/user/certificates')

        commit(SET_CERTIFICATES, response)
      } catch (err) {
        console.error(err)
      }
    }
  },

  async sendTicket({ commit }, payload) {
    try {
      const response = await this.$axios.$post(`/api/user/contact`, payload)

      return response
    } catch (error) {
      console.error(error) // eslint-disable-line
    }
  },

  async getUser({ commit }) {
    try {
      const response = await this.$axios.$get('/api/user')

      commit(SET_USER, response)
    } catch (error) {
      console.error(error)
    }
  },

  async getOrders({ commit }, { limit = 5, skip = 0, tracking = 0 }) {
    try {
      const response = await this.$axios.$get(
        `/api/user/orders?limit=${limit}&skip=${skip}&tracking=${tracking}`
      )

      commit(SET_ORDERS, response)
    } catch (err) {
      console.error(err)
    }
  },

  async updateCycleDate({ commit }, payload) {
    try {
      const response = await this.$axios.$put(`/api/user/cycle-date`, payload)

      commit(SET_USER, response)
    } catch (error) {
      console.error(error)
    }
  },

  async applyCertificate({ commit, dispatch }, payload) {
    try {
      const { user, totals } = await this.$axios.$post(
        '/api/user/certificates',
        payload
      )

      commit(SET_USER, user)
      commit(SET_TOTALS, totals, { root: true })
    } catch (error) {
      dispatch('ui/setAppMessage', error.response.data, { root: true })
    }
  },

  async toggleSubscription({ commit }, payload) {
    try {
      const user = await this.$axios.$put('/api/user/subscription', payload)

      commit(SET_USER, user)
    } catch (error) {
      console.error(error)
    }
  },

  setUser({ commit }, payload) {
    commit(SET_USER, payload)
  },

  setProductOrder({ commit }, payload) {
    commit(SET_PRODUCT_ORDER, payload)
  }
}

export const getters = {
  customerId(state) {
    return state.details?.id || null
  },

  testGroups(state) {
    return state.details?.testGroups || []
  },

  isCanceled(state) {
    return state.details?.subscription?.isCanceled || false
  },

  isPending(state) {
    return state.report?.isPending
  },

  // Is redundant with isGuest/isUser
  isLoggedIn(state, getters) {
    return Boolean(getters.customerId)
  },

  nextCycleDate(state) {
    return state.details?.subscription?.nextCycleDate?.replace(/-/g, '/')
  },

  isSubscriber(state) {
    return Boolean(state.details?.subscription)
  },

  isGuest(state) {
    return isEmpty(state.details)
  },

  isUser(state, getters) {
    return !getters.isGuest
  },

  userStatus(state) {
    return function (user = '') {
      const userStatus = parseUser(state.details)
      if (!user) {
        return userStatus
      }

      return userStatus === user
    }
  },

  /**
   * Check if user can cancel
   * test_id 30 - Online cancellation | 50% of legacy with 3+ orders (even sub ID), If legacy + under 3 orders, only CA and Int'l can see
   * test_id 30 | group_id 60 - [variant1] legacy with 3+ orders (even sub ID + CA and Int'l)
   * test_id 30 | group_id 61 - [control] only CA and Int'l can see
   * test_id 31 - Online cancellation | subscribed after 08/25/2020
   * test_id 31 | group_id 62 - [variant1] if 3+ orders can see online cancelations (+CA and Int'l)
   * test_id 31 | group_id 63 - [control] only CA and Int'l can see
   */
  canCancel(state, getters) {
    const isPlanComplete = state.orderCount >= planDuration(getters.currentPlan)
    const isCA = fromCa(state.details.shipToStateProvince)
    const isIntl = state.details.shipToCountryCode !== 'US'
    const isInTestGroup =
      getters.isInTestGroup(30, 60) ||
      getters.isInTestGroup(31, 32) ||
      getters.isInTestGroup(32, 65)

    return (
      !getters.isCanceled &&
      (((isCA || isIntl) && isPlanComplete) || isInTestGroup)
    )
  },

  isCurrentPlan(_, getters) {
    return function (planType) {
      return getters.currentPlan === planType
    }
  },

  isInTest(state, getters) {
    return function (testId) {
      return Boolean(getters.testGroups.find(group => group.test_id == testId))
    }
  },

  isInTestGroup(state, getters) {
    return function (testId, variantId) {
      const testGroup =
        getters.testGroups.find(group => group.test_id == testId) || false

      return Boolean(testGroup && testGroup.group_id == variantId)
    }
  },

  lastShipment(state) {
    return state.orders?.[0] || null
  },

  lastShipmentDate(_, getters) {
    return getters.lastShipment?.createdAt
  },

  currentPlan(state) {
    return state.details?.subscription?.plan
  },

  currentPlanName(state) {
    switch (state.details?.subscription?.plan) {
      case '90':
        return '3-Month Plan'
      case '6M':
        return '6-Month Plan'
      case '12M':
        return '12-Month Plan'
      case 'MTM':
        return 'Monthly'
      default:
        return ''
    }
  },

  certificates(state, getters, rootState) {
    if (!state.details) {
      return []
    }

    const remainingStoreCredits =
      state.details.storeCredits - rootState.cart.totals.storeCreditsToUse

    const storeCredits = remainingStoreCredits
      ? {
          description: 'Store Credit',
          discountAmount: remainingStoreCredits,
          discountType: 'TYPE_ACTUAL',
          isCertificate: 0
        }
      : []

    return state.certificates.concat(storeCredits)
  },

  isDiscountTier(state) {
    return function (isTier) {
      return isTier ? state.details.tier == isTier : state.details.tier
    }
  },

  shippingAddress(state) {
    return {
      firstName: state.details?.firstName || '',
      lastName: state.details?.lastName || '',
      address1: state.details?.shipToAddress1 || '',
      address2: state.details?.shipToAddress2 || '',
      city: state.details?.shipToCity || '',
      stateProvince: state.details?.shipToStateProvince || '',
      countryCode: state.details?.shipToCountryCode || '',
      postalCode: state.details?.shipToPostalCode || '',
      phone: state.details?.phone || ''
    }
  },

  billingAddress(state) {
    return {
      name: `${state.details?.firstName} ${state.details?.lastName}` || '',
      address1: state.details?.address1 || '',
      address2: state.details?.address2 || '',
      city: state.details?.city || '',
      stateProvince: state.details?.stateProvince || '',
      countryCode: state.details?.countryCode || '',
      postalCode: state.details?.postalCode || ''
    }
  },

  hasPaymentProfile(state) {
    return Boolean(state.details?.paymentProcessorId)
  }
}
